import { Body, Tooltip, Chip } from '@cognite/cogs.js';
import styled from 'styled-components/macro';
import { Maybe } from 'types/dt-graphql-apollo-v2';

type LabelObject = {
  text: Maybe<string>;
  key: Maybe<string>;
};

type LabelListDisplayProps = {
  labels?: Array<LabelObject>;
  limitLabels?: number;
  // Label component missing in COGS v9 → fallback to Body
  labelSize?: 'x-small' | 'small' | 'medium';
};

export const LabelListDisplay = ({
  labels = [],
  limitLabels = 3,
  labelSize = 'small',
}: LabelListDisplayProps) => {
  if (!labels?.length) {
    return <EmptyPlaceholder>No labels were set</EmptyPlaceholder>;
  }

  const existingLabels: LabelObject[] = labels.flatMap((label) =>
    label ? [label] : []
  );

  const visibleTags = existingLabels.slice(0, limitLabels);
  const tooltipTags = existingLabels.slice(limitLabels);

  return (
    <StyledLabelsList>
      {visibleTags?.map((tag) =>
        tag ? (
          <StyledLabel
            key={tag.key}
            label={tag.text?.toString()}
            size={labelSize}
            type="neutral"
          />
        ) : null
      )}
      {!!tooltipTags.length && (
        <Tooltip
          content={
            <StyledTooltipLabels>
              {tooltipTags.map((tag) =>
                tag ? (
                  <StyledLabel
                    key={tag.key}
                    label={tag.text?.toString()}
                    size={labelSize}
                    type="neutral"
                  />
                ) : null
              )}
            </StyledTooltipLabels>
          }
        >
          <StyledLabelTooltipCount
            size={labelSize}
            type="neutral"
            label={tooltipTags.length.toString()}
          />
        </Tooltip>
      )}
    </StyledLabelsList>
  );
};

const StyledLabelsList = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  flex: 1;
  align-items: center;
  justify-content: flex-start;
`;

const StyledTooltipLabels = styled(StyledLabelsList)`
  flex-wrap: wrap;
  max-width: 450px;
`;

const StyledLabel = styled(Chip)`
  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 150px;
  }
`;
const StyledLabelTooltipCount = styled(Chip)`
  display: flex;
  padding: 2px 6px 2px 2px;
  .cogs-label--icon {
    width: 12px;
  }
  &:hover {
    cursor: pointer;
  }
`;

const EmptyPlaceholder = styled.i`
  color: var(--cogs-greyscale-grey5);
`;
